import { useMemo } from 'react';

import { ActivateTransactionViewModel } from '@occc-app/dataAccessLayer/activateTransaction/queries';
import { ActivateTransactionFulfilledOfferFragment } from '@occc/graphql';
import { TransactionViewFooter } from '@occc-app/routes/Transactions/TransactionView/TransactionViewFooter';
import { TransactionViewFulfillment } from '@occc-app/routes/Transactions/TransactionView/TransactionViewFulfillment';
import { readableDateWithShortTimezone } from '@occc-app/lib/format';
import { TransactionViewPointsSummary } from './TransactionViewPointsSummary';

import { TransactionViewHeader } from './TransactionViewHeader';
import { TransactionViewItems } from './TransactionViewItems';
import {
  getActivateTransactionLocationAddress,
  getActivateTransactionPayWith,
  getActivateTransactionTotal,
  getActivateTransactionItems,
  getTransactionPurchaseType,
  getActivateTransactionStatusLabel,
} from './utils';

export const TransactionViewOfActivateTransaction = (props: {
  transaction: ActivateTransactionViewModel;
}) => {
  const {
    lineItems,
    pointTransactions,
    totalAmount,
    totalTax,
    totalDiscountAmount,
    subtotalAmount,
    transactionTimeAt,
    location,
    paymentInfos,
    offerFulfillments,
    createdAt,
    totalPointsSpent,
    totalPointsEarned,
    loyaltySequenceId,
    processedAt,
  } = props.transaction;

  const total = useMemo(
    () => totalAmount || getActivateTransactionTotal(lineItems), // if totalAmount is null or 0 use FE calculation
    [lineItems, totalAmount],
  );

  const items = useMemo(() => {
    return getActivateTransactionItems(lineItems);
  }, [lineItems]);

  const offers = useMemo(() => {
    return (
      offerFulfillments
        ?.map((fulfillment) => fulfillment?.fulfilledOffer)
        .filter((offer): offer is ActivateTransactionFulfilledOfferFragment => Boolean(offer)) || []
    );
  }, [offerFulfillments]);

  return (
    <>
      <TransactionViewHeader
        locationId={location?.id || ''}
        locationName={location?.name || ''}
        locationAddress={getActivateTransactionLocationAddress(location)}
        paidWith={getActivateTransactionPayWith(paymentInfos).toUpperCase()}
        completedAt={readableDateWithShortTimezone(transactionTimeAt)}
        startedAt={readableDateWithShortTimezone(createdAt)}
        processedAt={readableDateWithShortTimezone(processedAt)}
        loyaltySequenceId={loyaltySequenceId ?? undefined}
        pointsSpent={totalPointsSpent}
        pointsEarned={totalPointsEarned}
        total={total}
        discount={totalDiscountAmount ?? undefined}
        purchaseType={getTransactionPurchaseType(props.transaction) ?? undefined}
        status={getActivateTransactionStatusLabel(props.transaction.status) ?? undefined}
      />
      <TransactionViewItems items={items} />
      <TransactionViewPointsSummary
        totalPointsEarned={totalPointsEarned}
        totalPointsSpent={totalPointsSpent}
        pointTransactions={pointTransactions}
      />
      <TransactionViewFooter
        total={total}
        discount={totalDiscountAmount ?? undefined}
        tax={totalTax ?? undefined}
        subTotal={subtotalAmount ?? undefined}
        vendorName={props.transaction.vendorName}
      />
      {offers?.length ? <TransactionViewFulfillment offers={offers} /> : null}
    </>
  );
};
